import React, { useState } from 'react'
import Image from 'next/image'
import classes from './OrderHistoryCard.module.scss'
import crabTestImage from '../crab.png'
import { IconButton } from '@mui/material'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { DateTime } from 'luxon'

export default function OrderHistoryCard({ order, startExpanded }) {
  const [expanded, setExpanded] = useState(startExpanded ?? false)
  const isGiftOrder = order?.is_gift_order === 'true'
  
  const GIFT_CARD_TITLE = 'Gift Card';
  const isOnlyGiftCard = order?.line_items.every(item => item.title?.includes(GIFT_CARD_TITLE))

  const toggleAccordion = () => {
    setExpanded(!expanded)
  }

  const renderLineItems = (lineitems = []) => {
    return lineitems.map((item, index) => {
      return (
        <div key={`line-item-${item.title}-${index}`}>
          <div className={classes['line-item']} key={`line-item-${index}`}>
            <div className={classes['thumbnail']}>
              <Image
                src={item.product_image ?? crabTestImage.src}
                alt="Picture of lineitem"
                layout="intrinsic"
                height={80}
                width={120}
              />
              {isGiftOrder && <div className={classes['gift-banner']}>gift</div>}
            </div>
            <div className={classes['description']}>
              <div className={classes['headline']}>
                {item.quantity > 1 ? `${item.quantity}x ` : ''}
                {item.title}
              </div>
              <div className={classes['subtitle']}>{item.variant_title}</div>
            </div>
            <div className={classes['cost']}>
              <div className={classes['total']}>
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(item.price)}
              </div>
            </div>
          </div>
          {index + 1 !== lineitems.length && (
            <div className={classes['divider']}></div>
          )}
        </div>
      )
    })
  }

  const firstFulfillment = order.fulfillments[0]
  let estimatedDeliveryDate
  if (firstFulfillment) {
    estimatedDeliveryDate = !!firstFulfillment.estimated_delivery_date
      ? DateTime.fromISO(firstFulfillment.estimated_delivery_date).toFormat(
          'LLL d, yyyy',
        )
      : DateTime.fromISO(firstFulfillment.ship_date).toFormat('LLL d, yyyy')
  }

  const address = order.addresses[0] ?? { billing_address: 'Not provided' }

  return (
    <div className={classes['order-history-card']}>
      {/* Header */}
      <div className={classes['history-header']}>
        {/* Date and number */}
        <div className={classes['order-date']}>
          <div>
            {DateTime.fromISO(order.order_date).toFormat('LLL d, yyyy')}
          </div>
          <div className={classes['number']}>
            <span className={classes['cost']}>
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(order.total_price)}
              &nbsp;|&nbsp;
            </span>
            <span>{order.order_number}</span>
          </div>
        </div>

        {/* Estimated delivery/ship date */}
        {/* <div className={classes['delivery-date']}>
          {!!estimatedDeliveryDate && <div>Estimated Delivery</div>}
          {!!estimatedDeliveryDate && (
            <div className={classes['salmon']}>{estimatedDeliveryDate}</div>
          )}
        </div> */}

        {/* Address */}
        <div className={classes['address']}>
          <div>
            <strong>Ship to:</strong> {isOnlyGiftCard ? 'Digital Delivery': address.shipping_name}
          </div>
          <div>{address.shipping_address}</div>
          <div>
            {address.shipping_city}{isOnlyGiftCard ? '': ', '}{address.shipping_state}{' '}
            {address.shipping_zip}
          </div>
        </div>

        {/* Expansion Button */}
        <div className={classes['expansion-button']}>
          <IconButton aria-label="delete" onClick={toggleAccordion}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </div>
      </div>

      {/* Body of the panel */}
      <div
        className={`${classes['history-body']} ${
          expanded ? classes['expanded'] : classes['closed']
        }`}
      >
        <div className={classes['divider']}></div>
        {/* Line Items */}
        <div className={classes['line-items']}>
          {renderLineItems(order.line_items)}
        </div>

        <div className={classes['divider']}></div>

        <div className={classes['footer']}>
          <div className={classes['billing-address']}>
            <strong>Billing Address</strong>
            <div>{address?.billing_name}</div>
            <div>{address?.billing_address}</div>
            <div>
              {address?.billing_city} {address?.billing_state}{' '}
              {address?.billing_zip}
            </div>
          </div>

          <div className={classes['payment']}>
            <strong>Payment status</strong>
            <div>{order.order_status}</div>
          </div>

          {isGiftOrder && (
            <div className={classes['recipient-info']}>
              <strong>Recipient Info</strong>
              <div>{order.gift_recipient_name}</div>
              <div className={classes['recipient-email']}>{order.gift_recipient_email}</div>
            </div>
          )}

          <div className={classes['divider']}></div>

          <div className={classes['order-total']}>
            <div>
              <span>Subtotal</span>
              <span>
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(order.subtotal)}
              </span>
            </div>
            <div>
              <span>Discount</span>
              <span>
                -{' '}
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(order.discounts)}
              </span>
            </div>
            <div>
              <span>Shipping</span>
              <span>
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(order.shipping)}
              </span>
            </div>
            <div>
              <span>Tax</span>
              <span>
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(order.tax)}
              </span>
            </div>
            <div>
              <span>Total:</span>
              <span>
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(order.total_price)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
